import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { itemsAPI, vendorsAPI, locationsAPI, assetCheckoutAPI } from '../lib/api';
import RequireCap from '../components/auth/RequireCap';
import { useCap } from '../components/auth/RequireCap';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft, Save, Trash2, Upload, Image as ImageIcon, Plus, X, Star, Package, Minus, MapPin, History, ChevronDown, ChevronRight
} from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';
import { VendorSourcingCard } from '../components/item/VendorSourcingCard';
import { VariantVendorPricing } from '../components/item/VariantVendorPricing';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canEditAssets = useCap('assets', 'edit') || hasRole('admin');
  // 2026-08: IT unit fields (Serial, Asset Tag, IMEI, Phone, Vendor, Notes) are
  // chain-of-custody data — only sys admins can edit. Backend also enforces this
  // via a 403 on PUT /api/items/{id} when a non-admin tries to mutate an
  // it_assets item's variants array.
  const canEditITUnit = hasRole('admin');
  const isNew = id === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [item, setItem] = useState({
    name: '', category: '', asset_type: '', description: '',
    is_active: true, photo_required: false, serial_tracked: false,
    make: '', model: '', variants: [],
    // Item-level vendor sourcing (2026-02)
    preferred_vendor_id: '', vendor_links: []
  });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [newVariant, setNewVariant] = useState({
    size: '', gender: '', sku: '', price: 0, quantity: 0, min_stock: 0, max_stock: 100,
    vendor_id: '', vendor_sku: '', location_id: '',
    serial_number: '', asset_tag: '', service_tag: '', phone_number: '', imei: '', notes: ''
  });
  const [showVariantDialog, setShowVariantDialog] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  // Memoised active subsets — these are referenced multiple times inside JSX, so re-filtering on every render is wasteful (CR fix iter 35).
  const activeVendors = useMemo(() => vendors.filter(v => v.is_active), [vendors]);
  const activeLocations = useMemo(() => locations.filter(l => l.is_active), [locations]);
  const [inventoryRecords, setInventoryRecords] = useState([]);
  // Per-variant open-checkout map (variant_sku → checkout doc). Empty = all available.
  const [checkoutsBySku, setCheckoutsBySku] = useState({});
  // Per-variant full checkout history (variant_sku → [rows...]). Lazy-loaded when
  // a unit's history strip is first expanded. Also holds the set of expanded skus.
  const [historyBySku, setHistoryBySku] = useState({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistorySkus, setExpandedHistorySkus] = useState(new Set());
  // 2026-08 UX: unit-detail fields (Serial/IMEI/Notes/etc.) are collapsed by default.
  // Users click "Details" per unit to expand — keeps the Units tab scannable when
  // there are 50+ devices on the page.
  const [expandedDetailSkus, setExpandedDetailSkus] = useState(new Set());
  const toggleDetails = (sku) => {
    setExpandedDetailSkus(prev => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku); else next.add(sku);
      return next;
    });
  };
  // Adjustment dialog state
  const [adjustDialog, setAdjustDialog] = useState(null); // { variant_sku, location_id, location_name, current_qty }
  const [adjustAmount, setAdjustAmount] = useState(0);
  // Add stock dialog state
  const [addStockDialog, setAddStockDialog] = useState(null); // { variant_sku, variant_size }
  const [addStockLocation, setAddStockLocation] = useState('');
  const [addStockQty, setAddStockQty] = useState(0);
  const [removeVariantIndex, setRemoveVariantIndex] = useState(null);
  const [pendingPhotos, setPendingPhotos] = useState([]); // For new items: queued files before save

  const CATEGORIES = [
    { value: 'uniforms', label: 'Uniforms' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'it_assets', label: 'IT Assets' },
    { value: 'safety', label: 'Safety Gear' },
    { value: 'accessories', label: 'Accessories' },
  ];
  const IT_ASSET_TYPES = [
    { value: 'laptop', label: 'Laptop' }, { value: 'phone', label: 'Mobile Phone' },
    { value: 'tablet', label: 'Tablet' }, { value: 'radio', label: 'Two-Way Radio' },
    { value: 'camera', label: 'Body Camera' }, { value: 'other', label: 'Other' },
  ];
  const LAPTOP_MAKES = ['Dell', 'MacBook', 'Lenovo', 'HP'];
  const PHONE_MAKES = ['iPhone', 'Samsung', 'Google Pixel'];

  useEffect(() => {
    loadVendors();
    loadLocations();
    if (!isNew) { loadItem(); loadImages(); loadInventory(); loadCheckoutsByVariant(); }
  }, [id]);

  const loadVendors = async () => {
    try { const r = await vendorsAPI.list(); setVendors(r.data); } catch (e) { console.error(e); }
  };
  const loadCheckoutsByVariant = async () => {
    // 2026-08: pre-load per-variant open-checkout map so each IT unit (or any
    // serial-tracked variant) shows "Available" / "Checked out to <name>".
    try {
      const r = await assetCheckoutAPI.getStatusByVariant(id);
      setCheckoutsBySku(r.data || {});
    } catch (e) { /* non-fatal; badge just won't render */ }
  };
  // 2026-08 P2: Per-unit history panel. Called lazily on first expansion so
  // list-heavy items don't pay the DB cost until an admin asks for it.
  const loadHistoryByVariant = async () => {
    if (Object.keys(historyBySku).length > 0) return;
    setHistoryLoading(true);
    try {
      const r = await assetCheckoutAPI.getHistoryByVariant(id);
      setHistoryBySku(r.data || {});
    } catch (e) {
      toast.error('Could not load unit history');
    } finally {
      setHistoryLoading(false);
    }
  };
  const toggleHistory = async (sku) => {
    await loadHistoryByVariant();
    setExpandedHistorySkus(prev => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku); else next.add(sku);
      return next;
    });
  };
  const loadLocations = async () => {
    try { const r = await locationsAPI.list(); setLocations(r.data); } catch (e) { console.error(e); }
  };
  const loadItem = async () => {
    try {
      const r = await itemsAPI.get(id);
      // Ensure vendor_links is always an array so JSX iterators are safe on older items
      setItem({ ...r.data, vendor_links: r.data.vendor_links || [], preferred_vendor_id: r.data.preferred_vendor_id || '' });
    } catch (e) { toast.error('Failed to load item'); navigate('/items'); } finally { setLoading(false); }
  };
  const loadImages = async () => {
    try { const r = await itemsAPI.listImages(id); setImages(r.data); } catch (e) { console.error(e); }
  };
  const loadInventory = async () => {
    try { const r = await itemsAPI.getItemInventory(id); setInventoryRecords(r.data); } catch (e) { console.error(e); }
  };

  const handleSave = async () => {
    if (!item.name || !item.category) { toast.error('Name and category are required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const res = await itemsAPI.create(item);
        const newId = res.data?.id;
        // Upload queued photos
        if (newId && pendingPhotos.length > 0) {
          for (let i = 0; i < pendingPhotos.length; i++) {
            const formData = new FormData();
            formData.append('file', pendingPhotos[i]);
            formData.append('is_primary', i === 0);
            try { await itemsAPI.uploadImage(newId, formData); }
            catch (e) { toast.error(`Photo upload failed: ${pendingPhotos[i].name}`); }
          }
        }
        toast.success('Item created successfully');
      } else {
        await itemsAPI.update(id, item);
        toast.success('Item updated successfully');
      }
      navigate('/items');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete "${item.name}"?`)) return;
    try { await itemsAPI.delete(id); toast.success('Item deleted'); navigate('/items'); }
    catch (e) { toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed to delete'); }
  };

  // Image handlers
  const handleFileDrop = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    for (const file of files) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { toast.error(`${file.name}: Invalid type`); continue; }
      if (isNew) {
        // Queue photos for upload after save
        setPendingPhotos(prev => [...prev, file]);
        toast.success(`${file.name} queued — will upload on save`);
      } else {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('is_primary', images.length === 0);
        try { await itemsAPI.uploadImage(id, formData); toast.success(`${file.name} uploaded`); }
        catch (e) { toast.error(`${file.name}: Upload failed`); }
      }
    }
    if (!isNew) { loadImages(); setUploading(false); }
  }, [id, isNew, images.length]);

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleFileDrop(e.dataTransfer.files); };
  const handleSetPrimary = async (imgId) => { try { await itemsAPI.updateImage(id, imgId, { is_primary: true }); toast.success('Primary updated'); loadImages(); } catch (e) { toast.error('Failed'); } };
  const handleDeleteImage = async (imgId) => { try { await itemsAPI.deleteImage(id, imgId); toast.success('Deleted'); loadImages(); } catch (e) { toast.error('Failed'); } };

  // Variant helpers
  const generateSku = () => {
    const prefix = item.category === 'it_assets' ? (item.asset_type === 'laptop' ? 'LT' : item.asset_type === 'phone' ? 'PH' : 'IT') : item.name.replace(/[^A-Z]/gi, '').substring(0, 3).toUpperCase() || 'ITM';
    return `TC-${prefix}-${String((item.variants?.length || 0) + 1).padStart(3, '0')}`;
  };

  const addVariant = () => {
    const isIT = item.category === 'it_assets';
    const v = { ...newVariant };
    if (isIT) {
      if (!v.sku) v.sku = generateSku();
      if (!v.serial_number && !v.asset_tag) { toast.error('Enter Serial Number or Asset Tag'); return; }
    } else {
      if (!v.size) { toast.error('Size is required'); return; }
      if (!v.sku) v.sku = `${item.name.replace(/[^A-Z]/gi, '').substring(0, 4).toUpperCase()}-${v.size.toUpperCase()}`;
    }
    if (!isIT && v.quantity > 0 && !v.location_id) { toast.error('Select a location for initial stock'); return; }
    if (item.variants.some(ev => ev.sku === v.sku)) { toast.error(`SKU "${v.sku}" exists`); return; }
    setItem({ ...item, variants: [...item.variants, v] });
    setNewVariant({ size: '', gender: '', sku: '', price: 0, quantity: 0, min_stock: 0, max_stock: 100, vendor_id: '', vendor_sku: '', location_id: '', serial_number: '', asset_tag: '', service_tag: '', phone_number: '', imei: '', notes: '' });
    setShowVariantDialog(false);
    toast.success(`Variant ${v.sku} added`);
  };

  const removeVariant = (index) => {
    setItem({ ...item, variants: item.variants.filter((_, i) => i !== index) });
    setRemoveVariantIndex(null);
    toast.success('Variant removed');
  };
  const updateVariant = (index, field, value) => {
    if (field === 'vendor_id' && value === '_none') value = '';
    setItem({ ...item, variants: item.variants.map((v, i) => i === index ? { ...v, [field]: value } : v) });
  };
  const getVendorName = (vid) => vendors.find(v => v.id === vid)?.name || '';
  const getLocationName = (lid) => locations.find(l => l.id === lid)?.name || 'Unknown';

  // Get inventory records for a specific variant
  const getVariantInventory = (sku) => inventoryRecords.filter(r => r.variant_sku === sku);
  const getVariantTotal = (sku) => getVariantInventory(sku).reduce((sum, r) => sum + (r.quantity || 0), 0);

  // Adjust quantity at a location
  const handleAdjust = async () => {
    if (!adjustDialog || adjustAmount === 0) return;
    try {
      const res = await itemsAPI.adjustVariant(id, adjustDialog.variant_sku, adjustAmount, adjustDialog.location_id);
      toast.success(`Adjusted to ${res.data.new_quantity} at ${adjustDialog.location_name}`);
      setAdjustDialog(null);
      setAdjustAmount(0);
      loadInventory();
      loadItem();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to adjust'); }
  };

  // Add stock at a new location
  const handleAddStock = async () => {
    if (!addStockDialog || !addStockLocation || addStockQty <= 0) { toast.error('Select location and enter quantity'); return; }
    try {
      await itemsAPI.addVariantStock(id, addStockDialog.variant_sku, addStockLocation, addStockQty);
      toast.success('Stock added');
      setAddStockDialog(null);
      setAddStockLocation('');
      setAddStockQty(0);
      loadInventory();
      loadItem();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add stock'); }
  };

  if (loading) return <div className="space-y-6"><div className="h-8 w-48 skeleton-pulse"></div><div className="h-96 skeleton-pulse rounded-lg"></div></div>;

  return (
    <div className="space-y-6" data-testid="item-detail-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/items')}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="page-title">{isNew ? 'New Item' : item.name}</h1>
            {!isNew && <p className="text-slate-500 mt-1">Created {formatDate(item.created_at)}</p>}
          </div>
        </div>
        <div className="flex gap-3">
          {!isNew && <RequireCap module="assets" action="delete"><Button variant="destructive" onClick={handleDelete} data-testid="delete-item-btn"><Trash2 className="w-4 h-4 mr-2" /> Delete</Button></RequireCap>}
          <RequireCap module="assets" action={isNew ? 'add' : 'edit'}><Button onClick={handleSave} disabled={saving} data-testid="save-item-btn"><Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Item'}</Button></RequireCap>
        </div>
      </div>

      <Tabs defaultValue="details" className="space-y-6">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="variants">{item.category === 'it_assets' ? 'Units' : 'Variants'} ({item.variants?.length || 0})</TabsTrigger>
          <TabsTrigger value="images">Photos ({images.length})</TabsTrigger>
        </TabsList>

        {/* ===== DETAILS TAB ===== */}
        <TabsContent value="details" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader><CardTitle>Item Information</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input value={item.name} onChange={(e) => setItem({ ...item, name: e.target.value })} placeholder="e.g. Polo - Black - Short Sleeve" data-testid="item-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select value={item.category} onValueChange={(v) => setItem({ ...item, category: v, asset_type: v === 'it_assets' ? item.asset_type : '', make: v === 'it_assets' ? item.make : '', model: v === 'it_assets' ? item.model : '' })}>
                      <SelectTrigger data-testid="item-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {item.category === 'it_assets' && (
                  <div className="space-y-2">
                    <Label>Asset Type</Label>
                    <Select value={item.asset_type || ''} onValueChange={(v) => setItem({ ...item, asset_type: v, make: '', model: '' })}>
                      <SelectTrigger data-testid="item-asset-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>{IT_ASSET_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={item.description || ''} onChange={(e) => setItem({ ...item, description: e.target.value })} placeholder="Enter description..." rows={3} data-testid="item-description-input" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Settings</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={item.is_active} onCheckedChange={(c) => setItem({ ...item, is_active: c })} data-testid="item-active-switch" /></div>
                <div className="flex items-center justify-between"><Label>Photo Required</Label><Switch checked={item.photo_required} onCheckedChange={(c) => setItem({ ...item, photo_required: c })} /></div>
                {item.category === 'it_assets' && <div className="flex items-center justify-between"><Label>Serial Tracking</Label><Switch checked={item.serial_tracked} onCheckedChange={(c) => setItem({ ...item, serial_tracked: c })} /></div>}
                <div className="pt-2 border-t text-xs text-slate-500">Price, stock levels, and quantities are managed per variant in the Variants tab.</div>
              </CardContent>
            </Card>
          </div>
          {item.category === 'it_assets' && item.asset_type && (
            <Card>
              <CardHeader><CardTitle>{item.asset_type === 'laptop' ? 'Laptop' : item.asset_type === 'phone' ? 'Phone' : 'IT Asset'} Details</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Make</Label>
                  {['laptop', 'phone'].includes(item.asset_type) ? (
                    <Select value={item.make || ''} onValueChange={(v) => setItem({ ...item, make: v })}>
                      <SelectTrigger><SelectValue placeholder="Select make" /></SelectTrigger>
                      <SelectContent>{(item.asset_type === 'laptop' ? LAPTOP_MAKES : PHONE_MAKES).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <Input value={item.make || ''} onChange={(e) => setItem({ ...item, make: e.target.value })} />}
                </div>
                <div className="space-y-2"><Label>Model</Label><Input value={item.model || ''} onChange={(e) => setItem({ ...item, model: e.target.value })} placeholder="e.g. Latitude 5540" /></div>
              </CardContent>
            </Card>
          )}
          {/* Vendor sourcing — only surfaced for Uniforms and IT Assets today (2026-02) */}
          {(item.category === 'uniforms' || item.category === 'it_assets') && (
            <VendorSourcingCard
              item={item}
              vendors={vendors}
              activeVendors={activeVendors}
              canEdit={canEditAssets}
              onChange={setItem}
            />
          )}
        </TabsContent>

        {/* ===== VARIANTS TAB ===== */}
        <TabsContent value="variants" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{item.category === 'it_assets' ? 'Individual Units' : 'Size Variants'}</CardTitle>
                  <CardDescription>{item.category === 'it_assets' ? 'Each physical device as a unit' : 'Each variant tracks stock by location'}</CardDescription>
                </div>
                <Dialog open={showVariantDialog} onOpenChange={setShowVariantDialog}>
                  <DialogTrigger asChild>
                    {/* Adding an IT unit records serial/asset-tag — admin only.
                        For non-IT categories, warehouse/assets.edit is still enough. */}
                    {(item.category === 'it_assets' ? canEditITUnit : canEditAssets) ? (
                      <Button data-testid="add-variant-btn"><Plus className="w-4 h-4 mr-2" /> Add {item.category === 'it_assets' ? 'Unit' : 'Variant'}</Button>
                    ) : (
                      <Button data-testid="add-variant-btn" disabled title="Only sys admins can add IT units."><Plus className="w-4 h-4 mr-2" /> Add {item.category === 'it_assets' ? 'Unit' : 'Variant'}</Button>
                    )}
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Add {item.category === 'it_assets' ? 'Unit' : 'Variant'}</DialogTitle>
                      <DialogDescription>{item.category === 'it_assets' ? 'Add a device with its details' : 'Add a size variant with pricing, stock, and location'}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-1">
                      {item.category === 'it_assets' ? (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2"><Label>Serial Number *</Label><Input value={newVariant.serial_number} onChange={(e) => setNewVariant({ ...newVariant, serial_number: e.target.value })} placeholder="SN-12345678" data-testid="variant-serial-input" /></div>
                            <div className="space-y-2"><Label>Asset Tag</Label><Input value={newVariant.asset_tag} onChange={(e) => setNewVariant({ ...newVariant, asset_tag: e.target.value })} placeholder="AT-001" data-testid="variant-asset-tag-input" /></div>
                          </div>
                          <div className="space-y-2"><Label>SKU (auto if blank)</Label><Input value={newVariant.sku} onChange={(e) => setNewVariant({ ...newVariant, sku: e.target.value })} placeholder={generateSku()} data-testid="variant-sku-input" /></div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2"><Label>Price ($)</Label><Input type="number" step="0.01" value={newVariant.price} onChange={(e) => setNewVariant({ ...newVariant, price: parseFloat(e.target.value) || 0 })} data-testid="variant-price-input" /></div>
                            <div className="space-y-2">
                              <Label>Vendor</Label>
                              <Select value={newVariant.vendor_id || '_none'} onValueChange={(v) => setNewVariant({ ...newVariant, vendor_id: v === '_none' ? '' : v })}>
                                <SelectTrigger data-testid="variant-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                                <SelectContent><SelectItem value="_none">None</SelectItem>{activeVendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="space-y-2"><Label>Vendor SKU</Label><Input value={newVariant.vendor_sku || ''} onChange={(e) => setNewVariant({ ...newVariant, vendor_sku: e.target.value })} placeholder="Vendor's SKU" data-testid="variant-vendor-sku-input" /></div>
                          {item.asset_type === 'laptop' && <div className="space-y-2"><Label>Service Tag</Label><Input value={newVariant.service_tag} onChange={(e) => setNewVariant({ ...newVariant, service_tag: e.target.value })} placeholder="Windows service tag" /></div>}
                          {item.asset_type === 'phone' && (
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2"><Label>Phone Number</Label><Input value={newVariant.phone_number} onChange={(e) => setNewVariant({ ...newVariant, phone_number: e.target.value })} /></div>
                              <div className="space-y-2"><Label>IMEI</Label><Input value={newVariant.imei} onChange={(e) => setNewVariant({ ...newVariant, imei: e.target.value })} /></div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2"><Label>Size *</Label><Input value={newVariant.size} onChange={(e) => setNewVariant({ ...newVariant, size: e.target.value })} placeholder="XS, S, M, L, XL..." data-testid="variant-size-input" /></div>
                            <div className="space-y-2"><Label>SKU (auto if blank)</Label><Input value={newVariant.sku} onChange={(e) => setNewVariant({ ...newVariant, sku: e.target.value })} placeholder={`${item.name.replace(/[^A-Z]/gi, '').substring(0, 4).toUpperCase()}-${newVariant.size?.toUpperCase() || 'SIZE'}`} data-testid="variant-sku-input" /></div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2"><Label>Price ($) *</Label><Input type="number" step="0.01" value={newVariant.price} onChange={(e) => setNewVariant({ ...newVariant, price: parseFloat(e.target.value) || 0 })} data-testid="variant-price-input" /></div>
                            <div className="space-y-2">
                              <Label>Vendor</Label>
                              <Select value={newVariant.vendor_id || '_none'} onValueChange={(v) => setNewVariant({ ...newVariant, vendor_id: v === '_none' ? '' : v })}>
                                <SelectTrigger data-testid="variant-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                                <SelectContent><SelectItem value="_none">None</SelectItem>{activeVendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="space-y-2"><Label>Vendor SKU</Label><Input value={newVariant.vendor_sku || ''} onChange={(e) => setNewVariant({ ...newVariant, vendor_sku: e.target.value })} placeholder="Vendor's SKU" data-testid="variant-vendor-sku-input" /></div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2"><Label>Min Stock</Label><Input type="number" value={newVariant.min_stock} onChange={(e) => setNewVariant({ ...newVariant, min_stock: parseInt(e.target.value) || 0 })} data-testid="variant-min-stock-input" /></div>
                            <div className="space-y-2"><Label>Max Stock</Label><Input type="number" value={newVariant.max_stock} onChange={(e) => setNewVariant({ ...newVariant, max_stock: parseInt(e.target.value) || 0 })} data-testid="variant-max-stock-input" /></div>
                          </div>
                          <div className="border-t pt-4 space-y-2">
                            <p className="text-sm font-semibold text-slate-700">Initial Stock</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Warehouse *</Label>
                                <Select value={newVariant.location_id || '_none'} onValueChange={(v) => setNewVariant({ ...newVariant, location_id: v === '_none' ? '' : v })}>
                                  <SelectTrigger data-testid="variant-location-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                                  <SelectContent><SelectItem value="_none">No initial stock</SelectItem>{activeLocations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2"><Label>Quantity</Label><Input type="number" value={newVariant.quantity} onChange={(e) => setNewVariant({ ...newVariant, quantity: parseInt(e.target.value) || 0 })} data-testid="variant-quantity-input" /></div>
                            </div>
                          </div>
                        </>
                      )}
                      <Button onClick={addVariant} className="w-full" data-testid="save-variant-btn">Add {item.category === 'it_assets' ? 'Unit' : 'Variant'}</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {item.variants?.length > 0 ? (
                <div className="space-y-4">
                  {item.variants.map((variant, index) => {
                    const locInventory = getVariantInventory(variant.sku);
                    const totalQty = isNew ? (variant.quantity || 0) : getVariantTotal(variant.sku);
                    // Build a { vendor_id -> vendor } map once per variant render so the
                    // vendor pricing subcomponent can look up display names cheaply.
                    const vendorsById = vendors.reduce((acc, v) => { acc[v.id] = v; return acc; }, {});
                    const itemWithVendorMap = { ...item, _vendorsById: vendorsById };
                    return (
                      <div key={index} className="rounded-lg border overflow-hidden" data-testid={`variant-block-${index}`}>
                        {/* Variant Header Row */}
                        <div className="flex items-center gap-3 p-4 bg-slate-50 flex-wrap">
                          {item.category !== 'it_assets' && <span className="font-bold text-xl min-w-[40px]">{variant.size}</span>}
                          {(() => {
                            // SKU is the unit identifier — for IT assets it's tied to chain-of-custody, admin-only.
                            const skuLocked = item.category === 'it_assets' ? !canEditITUnit : !canEditAssets;
                            return (
                              <Input value={variant.sku} onChange={(e) => updateVariant(index, 'sku', e.target.value)} className="font-mono text-sm h-8 w-36" data-testid={`variant-sku-edit-${index}`} readOnly={skuLocked} disabled={skuLocked} />
                            );
                          })()}
                          {/* Checkout status badge — shows for IT assets (each variant = one unit)
                              and for any other category variant that has a serial_number set. */}
                          {(!isNew && (item.category === 'it_assets' || variant.serial_number)) && (() => {
                            const co = checkoutsBySku[variant.sku] || (variant.serial_number ? checkoutsBySku[variant.serial_number] : null);
                            if (co) {
                              return (
                                <Badge
                                  className="bg-orange-100 text-orange-800 border border-orange-300 text-xs cursor-help"
                                  title={`Checked out on ${(co.checked_out_at || '').slice(0, 10)}${co.location_name ? ' from ' + co.location_name : ''}`}
                                  data-testid={`unit-status-${index}`}
                                >
                                  Checked out{co.employee_name ? ` to ${co.employee_name}` : ''}
                                </Badge>
                              );
                            }
                            return (
                              <Badge
                                className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-xs"
                                data-testid={`unit-status-${index}`}
                              >
                                Available
                              </Badge>
                            );
                          })()}
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">$</span>
                            <Input type="number" step="0.01" value={variant.price || 0} onChange={(e) => updateVariant(index, 'price', parseFloat(e.target.value) || 0)} className="text-sm h-8 w-24" data-testid={`variant-price-edit-${index}`} readOnly={!canEditAssets} disabled={!canEditAssets} />
                          </div>
                          <div className="flex items-center gap-2 ml-auto">
                            {item.category !== 'it_assets' && (
                              <Badge variant="outline" className="text-sm font-bold">Total: {totalQty}</Badge>
                            )}
                            {item.category !== 'it_assets' && (
                              <div className="flex items-center gap-1 text-xs text-slate-500">
                                <span>Min:</span>
                                <Input type="number" min="0" value={variant.min_stock || 0} onChange={(e) => updateVariant(index, 'min_stock', parseInt(e.target.value) || 0)} className="h-6 w-16 text-xs text-center" data-testid={`variant-min-stock-${index}`} readOnly={!canEditAssets} disabled={!canEditAssets} />
                                <span>Max:</span>
                                <Input type="number" min="0" value={variant.max_stock || 0} onChange={(e) => updateVariant(index, 'max_stock', parseInt(e.target.value) || 0)} className="h-6 w-16 text-xs text-center" data-testid={`variant-max-stock-${index}`} readOnly={!canEditAssets} disabled={!canEditAssets} />
                              </div>
                            )}
                            {variant.min_stock > 0 && totalQty <= variant.min_stock && totalQty > 0 && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Low Stock</Badge>
                            )}
                            {/* Details toggle — IT units only. Collapsed by default so a page
                                with 50 devices stays scannable; click to reveal editable fields. */}
                            {item.category === 'it_assets' && (() => {
                              const sku = variant.sku;
                              const isOpen = expandedDetailSkus.has(sku);
                              return (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => toggleDetails(sku)}
                                  data-testid={`unit-details-toggle-${index}`}
                                  title="Show / hide unit details"
                                >
                                  {isOpen ? <ChevronDown className="w-3 h-3 mr-1" /> : <ChevronRight className="w-3 h-3 mr-1" />}
                                  Details
                                </Button>
                              );
                            })()}
                            {/* History toggle — visible for IT units and any serialised variant. */}
                            {!isNew && (item.category === 'it_assets' || variant.serial_number) && (() => {
                              const sku = variant.sku;
                              const isOpen = expandedHistorySkus.has(sku);
                              const hist = historyBySku[sku] || [];
                              const count = hist.length;
                              return (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => toggleHistory(sku)}
                                  data-testid={`unit-history-toggle-${index}`}
                                  title="Show checkout history for this unit"
                                >
                                  {isOpen ? <ChevronDown className="w-3 h-3 mr-1" /> : <ChevronRight className="w-3 h-3 mr-1" />}
                                  <History className="w-3 h-3 mr-1" />
                                  History{count > 0 ? ` (${count})` : ''}
                                </Button>
                              );
                            })()}
                            {!isNew && item.category !== 'it_assets' && (
                              <RequireCap module="assets" action="edit">
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setAddStockDialog({ variant_sku: variant.sku, variant_size: variant.size }); setAddStockLocation(''); setAddStockQty(0); }} data-testid={`add-stock-btn-${index}`}>
                                  <MapPin className="w-3 h-3 mr-1" /> Add Stock
                                </Button>
                              </RequireCap>
                            )}
                            {/* Removing an IT unit destroys chain-of-custody — admin only.
                                Non-IT variants still fall back to the standard assets.delete cap. */}
                            {item.category === 'it_assets' ? (
                              canEditITUnit && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setRemoveVariantIndex(index)} data-testid={`remove-variant-${index}`}><X className="w-4 h-4" /></Button>
                              )
                            ) : (
                              <RequireCap module="assets" action="delete">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setRemoveVariantIndex(index)} data-testid={`remove-variant-${index}`}><X className="w-4 h-4" /></Button>
                              </RequireCap>
                            )}
                          </div>
                        </div>

                        {/* Per-variant vendor pricing (2026-02) — one row per item-linked vendor */}
                        {item.category !== 'it_assets' && (
                          <VariantVendorPricing
                            item={itemWithVendorMap}
                            variant={variant}
                            variantIndex={index}
                            canEdit={canEditAssets}
                            onChange={updateVariant}
                          />
                        )}

                        {/* IT-asset unit details — every field editable inline so ops can
                            correct phone numbers, IMEI, asset tags, etc. without rebuilding
                            the record. Collapsed by default (2026-08 UX) — click Details in
                            the header row to expand. */}
                        {item.category === 'it_assets' && expandedDetailSkus.has(variant.sku) && (
                          <div className="p-4 bg-surface space-y-4" data-testid={`unit-details-${index}`}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-1">
                                <Label className="text-xs text-slate-600">Serial Number</Label>
                                <Input
                                  value={variant.serial_number || ''}
                                  onChange={(e) => updateVariant(index, 'serial_number', e.target.value)}
                                  placeholder="SN-12345678"
                                  data-testid={`unit-serial-${index}`}
                                  readOnly={!canEditITUnit}
                                  disabled={!canEditITUnit}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-slate-600">Asset Tag</Label>
                                <Input
                                  value={variant.asset_tag || ''}
                                  onChange={(e) => updateVariant(index, 'asset_tag', e.target.value)}
                                  placeholder="AT-001"
                                  data-testid={`unit-asset-tag-${index}`}
                                  readOnly={!canEditITUnit}
                                  disabled={!canEditITUnit}
                                />
                              </div>
                              {item.asset_type === 'laptop' && (
                                <div className="space-y-1 md:col-span-2">
                                  <Label className="text-xs text-slate-600">Service Tag</Label>
                                  <Input
                                    value={variant.service_tag || ''}
                                    onChange={(e) => updateVariant(index, 'service_tag', e.target.value)}
                                    placeholder="Windows service tag"
                                    data-testid={`unit-service-tag-${index}`}
                                    readOnly={!canEditITUnit}
                                    disabled={!canEditITUnit}
                                  />
                                </div>
                              )}
                              {item.asset_type === 'phone' && (
                                <>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-slate-600">Phone Number</Label>
                                    <Input
                                      value={variant.phone_number || ''}
                                      onChange={(e) => updateVariant(index, 'phone_number', e.target.value)}
                                      placeholder="(555) 123-4567"
                                      data-testid={`unit-phone-number-${index}`}
                                      readOnly={!canEditITUnit}
                                      disabled={!canEditITUnit}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-slate-600">IMEI</Label>
                                    <Input
                                      value={variant.imei || ''}
                                      onChange={(e) => updateVariant(index, 'imei', e.target.value)}
                                      placeholder="15-digit IMEI"
                                      data-testid={`unit-imei-${index}`}
                                      readOnly={!canEditITUnit}
                                      disabled={!canEditITUnit}
                                    />
                                  </div>
                                </>
                              )}
                              <div className="space-y-1">
                                <Label className="text-xs text-slate-600">Vendor</Label>
                                <Select
                                  value={variant.vendor_id || '_none'}
                                  onValueChange={(v) => updateVariant(index, 'vendor_id', v === '_none' ? '' : v)}
                                  disabled={!canEditITUnit}
                                >
                                  <SelectTrigger data-testid={`unit-vendor-${index}`}>
                                    <SelectValue placeholder="Select vendor" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="_none">None</SelectItem>
                                    {activeVendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-slate-600">Vendor SKU</Label>
                                <Input
                                  value={variant.vendor_sku || ''}
                                  onChange={(e) => updateVariant(index, 'vendor_sku', e.target.value)}
                                  placeholder="Vendor's SKU"
                                  data-testid={`unit-vendor-sku-${index}`}
                                  readOnly={!canEditITUnit}
                                  disabled={!canEditITUnit}
                                />
                              </div>
                              <div className="space-y-1 md:col-span-2">
                                <Label className="text-xs text-slate-600">Notes</Label>
                                <Input
                                  value={variant.notes || ''}
                                  onChange={(e) => updateVariant(index, 'notes', e.target.value)}
                                  placeholder="Condition, warranty, plan carrier, or anything else worth remembering about this unit"
                                  data-testid={`unit-notes-${index}`}
                                  readOnly={!canEditITUnit}
                                  disabled={!canEditITUnit}
                                />
                              </div>
                            </div>
                            {canEditITUnit ? (
                              <p className="text-xs text-slate-500 italic">
                                Edits save when you click <b>Save</b> at the top of the page.
                              </p>
                            ) : (
                              <p className="text-xs text-amber-700 italic" data-testid={`unit-details-locked-${index}`}>
                                Read-only — only <b>sys admins</b> can edit IT unit details (serial, asset tag, IMEI, phone, vendor, notes).
                              </p>
                            )}
                          </div>
                        )}

                        {/* Location sub-rows */}
                        {item.category !== 'it_assets' && !isNew && (
                          <div className="divide-y">
                            {locInventory.length > 0 ? locInventory.map((inv) => (
                              <div key={inv.location_id} className="flex items-center gap-4 px-6 py-3 pl-12 bg-surface" data-testid={`inv-row-${variant.sku}-${inv.location_id}`}>
                                <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <span className="text-sm font-medium w-40 truncate">{inv.location_name}</span>
                                <div className="flex items-center gap-1">
                                  <RequireCap module="assets" action="edit"
                                    fallback={<span className="font-bold text-lg w-20 text-center" data-testid={`loc-qty-readonly-${variant.sku}-${inv.location_id}`}>{inv.quantity}</span>}>
                                    <>
                                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => { setAdjustDialog({ variant_sku: variant.sku, location_id: inv.location_id, location_name: inv.location_name, current_qty: inv.quantity }); setAdjustAmount(-1); }} data-testid={`loc-qty-minus-${variant.sku}-${inv.location_id}`}>
                                        <Minus className="w-3 h-3" />
                                      </Button>
                                      <span className="font-bold text-lg w-12 text-center cursor-pointer" onClick={() => { setAdjustDialog({ variant_sku: variant.sku, location_id: inv.location_id, location_name: inv.location_name, current_qty: inv.quantity }); setAdjustAmount(0); }} data-testid={`loc-qty-${variant.sku}-${inv.location_id}`}>
                                        {inv.quantity}
                                      </span>
                                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => { setAdjustDialog({ variant_sku: variant.sku, location_id: inv.location_id, location_name: inv.location_name, current_qty: inv.quantity }); setAdjustAmount(1); }} data-testid={`loc-qty-plus-${variant.sku}-${inv.location_id}`}>
                                        <Plus className="w-3 h-3" />
                                      </Button>
                                    </>
                                  </RequireCap>
                                </div>
                              </div>
                            )) : (
                              <div className="px-6 py-3 pl-12 text-sm text-slate-400 italic">No stock at any location. Use &quot;Add Stock&quot; to assign inventory.</div>
                            )}
                          </div>
                        )}

                        {/* IT asset details row */}
                        {item.category === 'it_assets' && (
                          <div className="px-6 py-2 bg-surface text-sm text-slate-500">
                            {[variant.serial_number && `SN: ${variant.serial_number}`, variant.asset_tag && `Tag: ${variant.asset_tag}`, variant.service_tag && `ST: ${variant.service_tag}`, variant.phone_number && `Ph: ${variant.phone_number}`, variant.imei && `IMEI: ${variant.imei}`].filter(Boolean).join(' | ') || 'No details'}
                          </div>
                        )}

                        {/* Per-unit checkout history strip — inline expandable so admins
                            can trace who had this device, when, for how long, and why it
                            came back. Only renders when explicitly expanded. 2026-08 P2. */}
                        {!isNew && (item.category === 'it_assets' || variant.serial_number) && expandedHistorySkus.has(variant.sku) && (
                          <div className="border-t bg-slate-50 px-4 py-3" data-testid={`unit-history-strip-${index}`}>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1">
                                <History className="w-3 h-3" /> Checkout History
                              </h4>
                              {historyLoading && <span className="text-xs text-slate-500">Loading…</span>}
                            </div>
                            {(() => {
                              const rows = historyBySku[variant.sku] || [];
                              if (rows.length === 0) {
                                return <div className="text-xs text-slate-500 italic py-2">No prior checkouts recorded for this unit.</div>;
                              }
                              return (
                                <div className="overflow-x-auto rounded border bg-surface">
                                  <table className="w-full text-xs">
                                    <thead className="bg-slate-100 text-slate-600">
                                      <tr>
                                        <th className="text-left px-2 py-1.5 font-medium">Assignee</th>
                                        <th className="text-left px-2 py-1.5 font-medium">Checked Out</th>
                                        <th className="text-left px-2 py-1.5 font-medium">Returned</th>
                                        <th className="text-right px-2 py-1.5 font-medium">Duration</th>
                                        <th className="text-left px-2 py-1.5 font-medium">Condition</th>
                                        <th className="text-left px-2 py-1.5 font-medium">Notes</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                      {rows.map((h, hi) => {
                                        const isOpen = !h.checked_in_at;
                                        return (
                                          <tr key={h.id || hi} data-testid={`unit-history-row-${index}-${hi}`}>
                                            <td className="px-2 py-1.5">
                                              <span className={h.employee_masked ? 'italic text-slate-500' : ''}>
                                                {h.employee_name || h.location_name || '—'}
                                              </span>
                                              {isOpen && <Badge className="ml-1 bg-orange-100 text-orange-800 text-[10px] px-1 py-0">active</Badge>}
                                            </td>
                                            <td className="px-2 py-1.5 whitespace-nowrap">{formatDate(h.checked_out_at)}</td>
                                            <td className="px-2 py-1.5 whitespace-nowrap">{h.checked_in_at ? formatDate(h.checked_in_at) : <span className="text-slate-400">—</span>}</td>
                                            <td className="px-2 py-1.5 text-right whitespace-nowrap">{h.duration_days != null ? `${h.duration_days}d` : '—'}</td>
                                            <td className="px-2 py-1.5 capitalize">{h.condition_in || <span className="text-slate-400">—</span>}</td>
                                            <td className="px-2 py-1.5 max-w-[280px] truncate" title={[h.notes_out && `Out: ${h.notes_out}`, h.notes_in && `In: ${h.notes_in}`].filter(Boolean).join(' | ')}>
                                              {[h.notes_out && `Out: ${h.notes_out}`, h.notes_in && `In: ${h.notes_in}`].filter(Boolean).join(' | ') || <span className="text-slate-400">—</span>}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="font-medium">No {item.category === 'it_assets' ? 'units' : 'variants'} yet</p>
                  <p className="text-sm mt-1">{item.category === 'it_assets' ? 'Add physical devices as units' : 'Add size variants with pricing and stock locations'}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== IMAGES TAB ===== */}
          <TabsContent value="images" className="space-y-6">
            <Card>
              <CardHeader><CardTitle>Item Photos</CardTitle><CardDescription>Drag and drop images or click to upload</CardDescription></CardHeader>
              <CardContent className="space-y-4">
                {hasRole('admin', 'warehouse') && (
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400'}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('image-upload').click()}
                    data-testid="image-drop-zone"
                  >
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => { handleFileDrop(e.target.files); e.target.value = ''; }} className="hidden" id="image-upload" multiple disabled={uploading} />
                    <Upload className={`w-10 h-10 mx-auto mb-3 ${dragOver ? 'text-blue-500' : 'text-slate-400'}`} />
                    <p className="font-medium text-slate-700">{uploading ? 'Uploading...' : dragOver ? 'Drop here' : 'Drag & drop images here'}</p>
                    <p className="text-sm text-slate-500 mt-1">or click to browse (JPG, PNG, WebP)</p>
                  </div>
                )}
                {/* Queued photos for new items */}
                {isNew && pendingPhotos.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {pendingPhotos.map((file, idx) => (
                      <div key={idx} className="relative group rounded-lg overflow-hidden border">
                        <img src={URL.createObjectURL(file)} alt={file.name} className="w-full aspect-square object-cover" />
                        {idx === 0 && <Badge className="absolute top-2 left-2 bg-blue-600"><Star className="w-3 h-3 mr-1" /> Primary</Badge>}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <Button size="sm" variant="destructive" onClick={() => setPendingPhotos(prev => prev.filter((_, i) => i !== idx))} data-testid={`remove-pending-photo-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                        <p className="text-xs text-center p-1 truncate text-slate-500">{file.name}</p>
                      </div>
                    ))}
                  </div>
                )}
                {isNew && pendingPhotos.length === 0 && (
                  <div className="text-center py-8 text-slate-500"><ImageIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" /><p>No photos added yet</p><p className="text-sm mt-1">Photos will be uploaded when you save the item</p></div>
                )}
                {/* Saved photos for existing items */}
                {!isNew && images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {images.map((img) => (
                      <div key={img.id} className="relative group rounded-lg overflow-hidden border">
                        <img src={`${API_URL}${img.url}`} alt={img.alt_text || item.name} className="w-full aspect-square object-cover" />
                        {img.is_primary && <Badge className="absolute top-2 left-2 bg-blue-600"><Star className="w-3 h-3 mr-1" /> Primary</Badge>}
                        {hasRole('admin', 'warehouse') && (
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            {!img.is_primary && <Button size="sm" variant="secondary" onClick={() => handleSetPrimary(img.id)}><Star className="w-4 h-4" /></Button>}
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteImage(img.id)}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {!isNew && images.length === 0 && (
                  <div className="text-center py-8 text-slate-500"><ImageIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" /><p>No photos uploaded yet</p></div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
      </Tabs>

      {/* Adjust Quantity Dialog */}
      <Dialog open={!!adjustDialog} onOpenChange={(open) => { if (!open) { setAdjustDialog(null); setAdjustAmount(0); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Quantity</DialogTitle>
            <DialogDescription>{adjustDialog && `${item.name} at ${adjustDialog.location_name}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-sm text-slate-500">Current at this location</p>
              <p className="text-3xl font-bold">{adjustDialog?.current_qty || 0}</p>
            </div>
            <div className="space-y-2">
              <Label>Adjustment (+/-)</Label>
              <Input type="number" value={adjustAmount} onChange={(e) => setAdjustAmount(parseInt(e.target.value) || 0)} data-testid="variant-adjust-amount" />
              <p className="text-sm text-slate-500">New quantity: <strong>{Math.max(0, (adjustDialog?.current_qty || 0) + adjustAmount)}</strong></p>
            </div>
            <Button onClick={handleAdjust} className="w-full" data-testid="apply-variant-adjust">Apply Adjustment</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Stock at Location Dialog */}
      <Dialog open={!!addStockDialog} onOpenChange={(open) => { if (!open) { setAddStockDialog(null); setAddStockLocation(''); setAddStockQty(0); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Stock at Warehouse</DialogTitle>
            <DialogDescription>{addStockDialog && `${item.name} - ${addStockDialog.variant_size || addStockDialog.variant_sku}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Warehouse *</Label>
              <Select value={addStockLocation || '_none'} onValueChange={(v) => setAddStockLocation(v === '_none' ? '' : v)}>
                <SelectTrigger data-testid="add-stock-location-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">Select...</SelectItem>{activeLocations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input type="number" value={addStockQty} onChange={(e) => setAddStockQty(parseInt(e.target.value) || 0)} data-testid="add-stock-quantity" />
            </div>
            <Button onClick={handleAddStock} className="w-full" data-testid="apply-add-stock">Add Stock</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Variant Confirmation */}
      <AlertDialog open={removeVariantIndex !== null} onOpenChange={(open) => { if (!open) setRemoveVariantIndex(null); }}>
        <AlertDialogContent data-testid="remove-variant-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Variant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{removeVariantIndex !== null && item.variants[removeVariantIndex] ? `${item.variants[removeVariantIndex].size || item.variants[removeVariantIndex].sku}` : ''}</strong>? This will be saved when you click &quot;Save Item&quot;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-remove-variant">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => removeVariant(removeVariantIndex)} className="bg-red-600 hover:bg-red-700" data-testid="confirm-remove-variant">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
